import React, { useState, useEffect } from "react";
import { Box, Tabs, TabList, Tab, Accordion, Text, AspectRatio } from "@chakra-ui/react";
import { Section } from "components/section";
import { FillImage, DecorativeBackgroundImage } from "components/images";
import { GradientAccordionItem } from "ui/accordion/gradient-accordian-item";
import { AnimationVideo } from "ui/animation-video";
import useCase1Image from "../../../../public/assets/platform/agents/use-case-1.png";
import useCase2Image from "../../../../public/assets/platform/agents/use-case-2.png";
import useCase3Image from "../../../../public/assets/platform/agents/use-case-3.png";
import useCase4Image from "../../../../public/assets/platform/agents/use-case-4.png";
import useCase5Image from "../../../../public/assets/platform/agents/use-case-5.png";
import blueGradient from "../../../../public/assets/platform/agents/blue-gradient.png";

const ANIMATION_RATIO = 2640 / 1480;
const TAB_BG_GRADIENT = "linear-gradient(180deg, #CCF0F9 46.49%, #80C4EA 174.96%, #3498DA 303.42%, #164FAA 531.58%)";
const ACCORDION_BG_GRADIENT = "linear-gradient(180deg, #CCF0F9 23.71%, #8BCDF0 54.96%, #2D71DC 103.45%)";

// Shared data source
const useCases = [
  {
    id: "marketing-campaign",
    title: "Plan marketing campaigns",
    description: "Ideate and write winning campaign briefs",
    mobileImage: useCase1Image,
    mobileImageRatio: 343 / 450,
    animationMp4: "https://cdn.sanity.io/files/pwmfmi47/production/755e7142d994d7306158540f8e5b59bbd5e80791.mp4",
    duration: 12_000,
  },
  {
    id: "customer-data",
    title: "Ask any customer data question",
    description: "Stop waiting on data requests, get answers immediately",
    mobileImage: useCase2Image,
    mobileImageRatio: 343 / 340,
    animationMp4: "https://cdn.sanity.io/files/pwmfmi47/production/d0e2330fad9bc0fadc250320ab56d3be75253294.mp4",
    duration: 7000,
  },
  {
    id: "creative-analysis",
    title: "Analyze creative & copy",
    description: "Discover what is and isn’t working and how to improve",
    mobileImage: useCase3Image,
    mobileImageRatio: 343 / 380,
    animationMp4: "https://cdn.sanity.io/files/pwmfmi47/production/7b62e5749146919286a1981388a7721e729aa2bd.mp4",
    duration: 7000,
  },
  {
    id: "audience-discovery",
    title: "Discover new audiences",
    description: "Find the right audience for every campaign",
    mobileImage: useCase4Image,
    mobileImageRatio: 343 / 420,
    animationMp4: "https://cdn.sanity.io/files/pwmfmi47/production/1d2279fc9f893fdbbf8bc7db7ffd02de4ab292a5.mp4",
    duration: 7000,
  },
  {
    id: "automated-reporting",
    title: "Automate reporting",
    description: "Save hours every Monday, let AI take a first pass on “why”",
    mobileImage: useCase5Image,
    mobileImageRatio: 343 / 470,
    animationMp4: "https://cdn.sanity.io/files/pwmfmi47/production/24316001e7a6d622838f131f2f05fd6c08379838.mp4",
    duration: 13_000,
  },
];

export function AgentsUseCases() {
  return (
    <Section id="use-cases" my={0}>
      <Box display={{ base: "block", lg: "none" }} pt="tiny">
        <AccordionLayout />
      </Box>
      <Box display={{ base: "none", lg: "block" }}>
        <TabsLayout />
      </Box>
    </Section>
  );
}

/* ---------- Tabs Layout ---------- */

const ANIMATION_TRANSITION = {
  opacity: 1,
  y: 0,
  scale: 1,
  transition: { duration: 0.8, ease: [0.33, 1, 0.68, 1] },
};

function TabsLayout() {
  const [activeTab, setActiveTab] = useState(0);
  const [startAnim, setStartAnim] = useState(false);

  // Handle tab change: manage video playback and auto-progression
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTab((previous) => (previous === useCases.length - 1 ? 0 : previous + 1));
    }, useCases[activeTab].duration);

    return () => {
      clearInterval(interval);
    };
  }, [activeTab]);

  // Handle scroll-based animation start
  useEffect(() => {
    const timer = setTimeout(() => {
      setStartAnim(true);
    }, 3800);

    const handleScroll = () => {
      setStartAnim(true);
      clearTimeout(timer);
      window.removeEventListener("scroll", handleScroll);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <Box
      maxW="1000px"
      mx="auto"
      opacity={startAnim ? 1 : 0}
      transform={startAnim ? "translateY(0) scale(1)" : "translateY(40px) scale(0.98)"}
      transition="opacity 0.8s cubic-bezier(0.33, 1, 0.68, 1), transform 0.8s cubic-bezier(0.33, 1, 0.68, 1)"
      willChange={startAnim ? "auto" : "opacity, transform"}
    >
      <Tabs index={activeTab} variant="unstyled" onChange={setActiveTab}>
        <TabList overflowX="auto" gap="smaller" flexWrap={{ base: "wrap", md: "nowrap" }}>
          {useCases.map((uc, i) => (
            <UseCaseTab key={uc.id} title={uc.title} description={uc.description} isSelected={activeTab === i} />
          ))}
        </TabList>

        <Box position="relative" h="auto" mt="smaller" borderRadius="smaller" overflow="hidden">
          <Box pos="absolute" inset={0} zIndex={0}>
            <DecorativeBackgroundImage src={blueGradient} style={{ objectFit: "cover" }} />
          </Box>

          {/* Keep all panels mounted and use CSS to show/hide to prevent flashing */}
          <Box position="relative" zIndex={1} h="auto">
            {useCases.map((uc, index) => (
              <Box
                key={uc.id}
                position={index === activeTab ? "relative" : "absolute"}
                top={0}
                left={0}
                right={0}
                opacity={index === activeTab ? 1 : 0}
                visibility={index === activeTab ? "visible" : "hidden"}
                pointerEvents={index === activeTab ? "auto" : "none"}
                transition="opacity 0.2s ease-in-out"
                h="100%"
                p={0}
              >
                <AspectRatio ratio={ANIMATION_RATIO} w="100%" overflow="hidden">
                  <AnimationVideo
                    key={`${uc.id}-${index === activeTab ? activeTab : "inactive"}`}
                    isLooped
                    hideControls
                    mp4={uc.animationMp4}
                    poster=""
                    viewportAmount={0.7}
                  />
                </AspectRatio>
              </Box>
            ))}
          </Box>
        </Box>
      </Tabs>
    </Box>
  );
}

/* ---------- Accordion Layout ---------- */

function AccordionLayout() {
  return (
    <Accordion allowToggle defaultIndex={0} display="flex" flexDir="column" gap="smaller">
      {useCases.map((uc) => (
        <GradientAccordionItem
          key={uc.id}
          title={uc.title}
          description={uc.description}
          media={<FillImage src={uc.mobileImage} alt={uc.title} />}
          mediaRatio={uc.mobileImageRatio}
          activeGradientColor={ACCORDION_BG_GRADIENT}
          inactiveBorderColor="new.blue.300"
          buttonColor="new.blue.50"
        />
      ))}
    </Accordion>
  );
}

/* ---------- Shared Tab Component ---------- */

function UseCaseTab({ title, description, isSelected }: { title: string; description: string; isSelected: boolean }) {
  return (
    <Tab
      position="relative"
      display="flex"
      flexDir="column"
      justifyContent="flex-start"
      alignItems="flex-start"
      p="smaller"
      gap="tiny"
      borderRadius="tiny"
      border="1px solid"
      borderColor={isSelected ? "new.blue.150" : "new.blue.500"}
      transition="border-color 0.1s ease-out"
      w="100%"
      className="group"
      bg="white"
      overflow="hidden"
      _before={{
        content: '""',
        position: "absolute",
        inset: 0,
        bg: TAB_BG_GRADIENT,
        opacity: isSelected ? 1 : 0,
        transition: "opacity 0.2s ease-out",
        pointerEvents: "none",
        zIndex: 0,
      }}
      _hover={{
        _before: {
          opacity: isSelected ? 1 : 0.25,
        },
      }}
    >
      <Text
        as="h3"
        position="relative"
        zIndex={1}
        fontSize="16px"
        fontWeight="bold"
        textAlign="left"
        lineHeight={1.1}
        color={isSelected ? "" : "brand.text.secondary"}
        transition="color 0.1s ease-out"
      >
        {title}
      </Text>
      <Text
        position="relative"
        zIndex={1}
        fontSize="12px"
        textAlign="left"
        color={isSelected ? "" : "brand.text.secondary"}
        transition="color 0.2s ease-out"
      >
        {description}
      </Text>
    </Tab>
  );
}
